import { Text, HStack, VStack, Box } from '@chakra-ui/react';
import { CallStatus } from '@halo/chat-sdk';
import {
  Avatar,
  VoiceIcon,
  VideoConnectingIcon,
  CallEncryptedIcon,
  MediaActionType,
} from '@halo/ui-chat-react';
import { useTranslation } from 'react-i18next';

export function CallStatusDesc({
  isSingle,
  isCaller = true,
  caller,
  callStatus = undefined,
  mediaType,
  fullScreen = false,
  callMembers,
}: {
  isSingle: boolean;
  isCaller: boolean;
  caller?: any;
  callStatus?: CallStatus;
  mediaType?: MediaActionType;
  fullScreen?: boolean;
  callMembers?: any[];
}) {
  const { t } = useTranslation();
  const callMembersLength = useMemo(() => callMembers?.length ?? 0, [callMembers]);

  const StatusIcon = useMemo(() => {
    switch (callStatus) {
      case CallStatus.RINGING:
        return <VoiceIcon />;
      case CallStatus.CONNECTING:
        if (isCaller) {
          return <VideoConnectingIcon />;
        } else {
          return mediaType === MediaActionType.AUDIO || !fullScreen ? (
            <VideoConnectingIcon />
          ) : (
            <CallEncryptedIcon />
          );
        }
    }
  }, [callStatus, fullScreen, isCaller, mediaType]);

  const StatusDsec = useMemo(() => {
    switch (callStatus) {
      case CallStatus.RINGING:
        if (isCaller) {
          return t('calls.statusRinging');
        } else {
          return `${mediaType === MediaActionType.AUDIO ? t('calls.statusVoiceCall') : t('calls.statusVideoCall')}`;
        }
      case CallStatus.CONNECTING:
        return mediaType === MediaActionType.AUDIO || !fullScreen
          ? t('calls.statusConnecting')
          : t('calls.statusEndToEndEncrypted');
    }
  }, [callStatus, fullScreen, isCaller, mediaType, t]);

  const GroupUI = useMemo(() => {
    return (
      <VStack gap="1" lineHeight="normal">
        <HStack gap="1" w="full" color="white" justify="center">
          <Box w="16px" h="16px">
            <VoiceIcon />
          </Box>

          <Text
            lineHeight="normal"
            fontWeight="normal"
            fontSize="sm"
            opacity="0.8"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {t('calls.waitingForCaller', { name: caller?.name })}
          </Text>
        </HStack>

        {callMembersLength > 0 && (
          <Text fontWeight="normal" fontSize="xs" opacity="0.8" lineHeight="normal">
            {t('calls.othersCalled', { count: callMembersLength })}
          </Text>
        )}
        <HStack alignItems="center" justifyContent="center" spaceX="-3">
          {callMembers?.slice(0, 3)?.map((member: any) => (
            <Avatar
              src={member?.avatar as string}
              key={member?.userId}
              w="24px"
              h="24px"
              css={{
                outlineWidth: '1px',
                outlineColor: 'bg',
                outlineStyle: 'solid',
              }}
            />
          ))}
          {callMembersLength > 3 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              px="1.5"
              zIndex={10}
              bg="blackAlpha.70"
              rounded="14px"
              fontSize="xs"
              h="28px"
              boxSizing="border-box"
            >
              +{callMembersLength > 102 ? 99 : callMembersLength - 3}
            </Box>
          )}
        </HStack>
      </VStack>
    );
  }, [caller?.name, callMembersLength, callMembers, t]);

  const SingleUI = useMemo(
    () => (
      <HStack gap="1" py="1" mb="12" w="full" justifyContent="center">
        {StatusIcon}
        <Box opacity="0.8" color="white">
          {StatusDsec}
        </Box>
      </HStack>
    ),
    [StatusIcon, StatusDsec]
  );
  if (isSingle) {
    return SingleUI;
  }

  return <>{isCaller ? SingleUI : GroupUI}</>;
}

export default memo(CallStatusDesc);
